import { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  SideDrawer,
  Button,
  SearchInput,
  CustomSelect,
  SkeletonBox,
  SkeletonText,
} from '@/components';
import { useQuotationPdfTemplates } from '../hooks';
import {
  DEFAULT_PDF_TEMPLATE_KEY,
  resolveQuotationMediaUrl,
  preloadPdfPreviews,
} from '../utils';
import {
  TemplatePdfThumbnail,
  TemplatePdfViewer,
} from './TemplatePdfThumbnail';

/**
 * Full detailed preview modal for a single template.
 * Canvas PDF viewer — no native iframe (dark chrome).
 */
function TemplateDetailModal({
  template,
  companyId,
  isOpen,
  onClose,
  onSelect,
  isSelected,
}) {
  const thumb = resolveQuotationMediaUrl(template?.preview_image_url);

  if (!template) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="inline-flex items-center gap-2.5 flex-wrap">
          <span>{template.name}</span>
          {template.is_popular && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary text-white shadow-xs tracking-normal">
              ★ Most Popular
            </span>
          )}
        </span>
      }
      description={
        template.category
          ? `${template.category} PDF Quotation Template`
          : 'PDF Quotation Template'
      }
      size="2xl"
      zIndexClass="z-[110]"
    >
      <div className="flex flex-col gap-5">
        <div className="relative w-full rounded-xl overflow-hidden border border-border-default min-h-[320px] h-[60vh]">
          <TemplatePdfViewer
            companyId={companyId}
            templateKey={template.key}
            templateName={template.name}
            enabled={isOpen}
            onErrorFallback={
              thumb ? (
                <img
                  src={thumb}
                  alt=""
                  className="mt-2 max-h-40 w-auto object-contain rounded-md border border-border-default opacity-80"
                />
              ) : null
            }
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-border-default">
          <Button type="button" variant="secondary" onClick={onClose}>
            Back to Templates
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              onSelect(template.key);
              onClose();
            }}
          >
            {isSelected ? 'Keep This Template' : 'Use This Template'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Single Template Card within the selection grid.
 */
function TemplateGridCard({
  template,
  isSelected,
  onSelect,
  onPreview,
  disabled,
  companyId,
  priority = 5,
}) {
  return (
    <div
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={() => !disabled && onSelect(template.key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!disabled) onSelect(template.key);
        }
      }}
      className={`group relative flex flex-col text-left rounded-xl border bg-white overflow-hidden transition-all outline-hidden cursor-pointer ${
        isSelected
          ? 'border-primary ring-2 ring-primary/25 shadow-md'
          : 'border-border-default hover:border-primary/50 hover:shadow-sm hover:-translate-y-0.5'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      {/* Thumbnail Container */}
      <div className="relative aspect-[16/10] bg-[#F5F5F5] overflow-hidden">
        <TemplatePdfThumbnail
          companyId={companyId}
          template={template}
          priority={priority}
        />

        {/* Popular Tag */}
        {template.is_popular && (
          <span className="absolute top-2.5 left-2.5 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-primary text-white shadow-sm">
            Popular
          </span>
        )}

        {/* Selected Checkmark Badge */}
        {isSelected && (
          <span className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="2 6 5 9 10 3" />
            </svg>
          </span>
        )}

        {/* Hover Quick Preview Button */}
        <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 p-3 backdrop-blur-[1px]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(template);
            }}
            className="px-3 py-1.5 rounded-lg bg-white/95 hover:bg-white text-text-primary text-xs font-medium shadow-md transition-transform active:scale-95 flex items-center gap-1.5"
          >
            <svg
              className="w-3.5 h-3.5 text-text-secondary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>Preview</span>
          </button>
        </div>
      </div>

      {/* Card Info */}
      <div className="p-3 sm:p-3.5">
        <div className="flex items-center justify-between gap-1.5">
          <p className="text-sm font-semibold text-text-primary leading-snug truncate">
            {template.name}
          </p>
          {template.category && (
            <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#F0F7F0] text-primary">
              {template.category}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Dedicated Template Selection Modal.
 * Used during Step 1 of "New Quotation" and when "Change Template" is clicked in form.
 */
export function QuotationTemplateSelectModal({
  isOpen,
  onClose,
  onSelect,
  companyId,
  selectedKey,
  title = 'Select Quotation Template',
  description = 'Choose a PDF quotation design for your catalog. You can customize details in the next step.',
  confirmButtonText = 'Continue to Quotation',
}) {
  const [currentSelectedKey, setCurrentSelectedKey] = useState(
    selectedKey || DEFAULT_PDF_TEMPLATE_KEY,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [previewingTemplate, setPreviewingTemplate] = useState(null);

  // Sync selectedKey prop when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentSelectedKey(selectedKey || DEFAULT_PDF_TEMPLATE_KEY);
      setSearchQuery('');
      setSelectedCategory('All');
    }
  }, [isOpen, selectedKey]);

  const { data, isLoading, isError, refetch } =
    useQuotationPdfTemplates(companyId);

  const rawTemplates = useMemo(() => {
    const list = Array.isArray(data) ? data : data?.results || [];
    return list;
  }, [data]);

  // Set default selection if none currently selected once templates load
  useEffect(() => {
    if (rawTemplates.length > 0 && !currentSelectedKey) {
      const defaultTpl =
        rawTemplates.find((t) => t.key === DEFAULT_PDF_TEMPLATE_KEY) ||
        rawTemplates[0];
      if (defaultTpl?.key) {
        setCurrentSelectedKey(defaultTpl.key);
      }
    }
  }, [rawTemplates, currentSelectedKey]);

  // Preload PDF previews sequentially in the background so all cards load quickly
  useEffect(() => {
    if (companyId && rawTemplates?.length > 0) {
      preloadPdfPreviews(companyId, rawTemplates, currentSelectedKey);
    }
  }, [companyId, rawTemplates, currentSelectedKey]);

  // Extract unique categories for filter dropdown (supports 50+ templates)
  const categories = useMemo(() => {
    const set = new Set();
    rawTemplates.forEach((t) => {
      if (t.category) set.add(t.category);
    });
    return ['All', ...Array.from(set).sort()];
  }, [rawTemplates]);

  // Options with live counts for Category dropdown
  const categoryOptions = useMemo(() => {
    return categories.map((cat) => {
      const count =
        cat === 'All'
          ? rawTemplates.length
          : rawTemplates.filter((t) => t.category === cat).length;
      return {
        value: cat,
        label: `${cat} (${count})`,
      };
    });
  }, [categories, rawTemplates]);

  // Filter templates based on search & category
  const filteredTemplates = useMemo(() => {
    return rawTemplates.filter((t) => {
      const matchesCat =
        selectedCategory === 'All' ||
        t.category?.toLowerCase() === selectedCategory.toLowerCase();
      if (!matchesCat) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      return (
        t.name?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q) ||
        t.key?.toLowerCase().includes(q)
      );
    });
  }, [rawTemplates, selectedCategory, searchQuery]);

  const activeSelectedTemplate = rawTemplates.find(
    (t) => t.key === currentSelectedKey,
  );

  const handleConfirm = () => {
    if (!currentSelectedKey) return;
    onSelect(currentSelectedKey);
    onClose();
  };

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        className="q-drawer"
        title={title}
        description={description}
        width="xl"
        bodyClassName="p-4 sm:p-5 flex flex-col gap-4 overflow-hidden"
        footer={
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-2 text-xs text-text-secondary w-full sm:w-auto">
              <span className="font-medium text-text-muted">Selected:</span>
              {activeSelectedTemplate ? (
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#F0F7F0] border border-primary/20 text-primary font-semibold">
                  <span>{activeSelectedTemplate.name}</span>
                  {activeSelectedTemplate.category && (
                    <span className="text-[10px] text-text-muted font-normal">
                      ({activeSelectedTemplate.category})
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-text-muted italic">
                  Please pick a template
                </span>
              )}
            </div>
            <div className="flex items-center justify-end gap-2.5 w-full sm:w-auto">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="w-full sm:w-auto text-xs sm:text-sm h-9 px-4"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!currentSelectedKey}
                onClick={handleConfirm}
                className="w-full sm:w-auto text-xs sm:text-sm h-9 px-5"
              >
                {confirmButtonText}
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-4 overflow-hidden h-full">
          {/* Controls Bar: Search & Category Filter Dropdown */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0 pb-3 border-b border-border-default/60">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto flex-1">
              <div className="w-full sm:w-64">
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates…"
                  id="template-modal-search"
                  className="w-full"
                  inputClassName="!h-9 !rounded-xl text-xs"
                />
              </div>

              <div className="w-full sm:w-52">
                <CustomSelect
                  value={selectedCategory}
                  onChange={(val) => setSelectedCategory(val || 'All')}
                  options={categoryOptions}
                  placeholder="All"
                  textSizeClass="text-xs"
                  className="w-full h-9"
                  buttonClassName="w-full h-9 flex items-center justify-between rounded-xl bg-white border border-border-default hover:border-primary/50 text-text-primary text-xs font-medium focus:outline-none focus:border-primary px-3 transition-all cursor-pointer shadow-2xs"
                  dropdownWidth={220}
                  hideCheckIcon={false}
                />
              </div>
            </div>

            <div className="text-xs text-text-muted shrink-0 text-left sm:text-right font-medium">
              {filteredTemplates.length}{' '}
              {filteredTemplates.length === 1 ? 'template' : 'templates'}
            </div>
          </div>

          {/* Template Grid Container */}
          <div className="overflow-y-auto flex-1 custom-scrollbar pr-1 -mr-1">
            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 py-1">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-border-default overflow-hidden bg-white"
                  >
                    <div className="relative aspect-[16/10] w-full">
                      <SkeletonBox className="absolute inset-0 w-full h-full rounded-none" />
                      <div className="absolute inset-0 p-3 flex flex-col pointer-events-none">
                        <div className="h-2 w-full rounded-sm bg-white/50 mb-2.5" />
                        <div className="space-y-1.5 flex-1">
                          <div className="h-1.5 w-full rounded-sm bg-white/40" />
                          <div className="h-1.5 w-[85%] rounded-sm bg-white/35" />
                          <div className="h-1.5 w-[70%] rounded-sm bg-white/30" />
                        </div>
                      </div>
                    </div>
                    <div className="px-3 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <SkeletonText width="w-2/3" height="h-3.5" />
                        <SkeletonText width="w-14" height="h-4" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="my-8 rounded-xl border border-status-error/20 bg-status-error/5 p-6 text-center">
                <p className="text-sm font-semibold text-status-error">
                  Failed to load quotation templates
                </p>
                <p className="text-xs text-text-secondary mt-1 max-w-md mx-auto">
                  There was an issue fetching available designs from the server.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => refetch()}
                  className="mt-4 text-xs h-8 px-3"
                >
                  Retry
                </Button>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="my-10 text-center py-8 rounded-xl border border-dashed border-border-default bg-[#FDFDFD]">
                <p className="text-sm font-semibold text-text-primary">
                  No templates match your filters
                </p>
                <p className="text-xs text-text-muted mt-1">
                  Try clearing your search query or selecting another category.
                </p>
                {(searchQuery || selectedCategory !== 'All') && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('All');
                    }}
                    className="mt-3 text-xs h-8 px-3"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 py-1">
                {filteredTemplates.map((template, idx) => (
                  <TemplateGridCard
                    key={template.key}
                    template={template}
                    companyId={companyId}
                    priority={
                      template.key === currentSelectedKey
                        ? 20
                        : Math.max(1, 10 - idx)
                    }
                    isSelected={template.key === currentSelectedKey}
                    onSelect={(key) => setCurrentSelectedKey(key)}
                    onPreview={(tpl) => setPreviewingTemplate(tpl)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </SideDrawer>

      {/* Enlarged Template Preview Modal */}
      {previewingTemplate && (
        <TemplateDetailModal
          template={previewingTemplate}
          companyId={companyId}
          isOpen={Boolean(previewingTemplate)}
          isSelected={previewingTemplate.key === currentSelectedKey}
          onClose={() => setPreviewingTemplate(null)}
          onSelect={(key) => {
            setCurrentSelectedKey(key);
            setPreviewingTemplate(null);
          }}
        />
      )}
    </>
  );
}

export default QuotationTemplateSelectModal;
